import { useEffect } from "react";

// Default OG image = Dr Sidhu's flagship hero portrait — used for every page unless overridden.
const DEFAULT_OG_IMAGE = "https://customer-assets-v7afamib.emergentagent.net/job_vascular-specialist/artifacts/h42j5vhh__DSC9521.webp";
const SITE_ORIGIN = "https://drtegbirvascular.com";

/**
 * Route-level SEO — sets document.title, meta description, keywords, canonical link,
 * Open Graph + Twitter card tags, robots directives, and hreflang. Cleans up on unmount.
 *
 * Props:
 *   title, description, keywords, image, url, type
 *   robots        — override the default index/follow directive (e.g., "noindex, follow")
 *   article       — { publishedTime, modifiedTime, section, tags[] } for og:type=article
 */
export default function SEO({ title, description, keywords, image, url, type = "website", robots, article }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const restorers = [];

    const setMeta = (selector, identity, content) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        Object.entries(identity).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      restorers.push(() => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
    };

    const setLink = (rel, href, extra = {}) => {
      const selector = `link[rel="${rel}"]${extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ""}`;
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      restorers.push(() => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("href", prev);
      });
    };

    // -- Description / keywords / title -------------------------------------
    if (description) {
      setMeta('meta[name="description"]', { name: "description" }, description);
      setMeta('meta[property="og:description"]', { property: "og:description" }, description);
      setMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description);
    }
    if (keywords && keywords.length) {
      setMeta('meta[name="keywords"]', { name: "keywords" }, keywords.join(", "));
    }
    if (title) {
      setMeta('meta[property="og:title"]', { property: "og:title" }, title);
      setMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title);
    }

    // -- OG / Twitter image & type ------------------------------------------
    const img = image || DEFAULT_OG_IMAGE;
    setMeta('meta[property="og:image"]', { property: "og:image" }, img);
    setMeta('meta[property="og:image:alt"]', { property: "og:image:alt" }, "Dr Tegbir Singh Sidhu — Interventional Radiologist");
    setMeta('meta[name="twitter:image"]', { name: "twitter:image" }, img);
    setMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt" }, "Dr Tegbir Singh Sidhu — Interventional Radiologist");
    setMeta('meta[property="og:type"]', { property: "og:type" }, type);
    setMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    setMeta('meta[property="og:site_name"]', { property: "og:site_name" }, "Dr Tegbir Vascular");
    setMeta('meta[property="og:locale"]', { property: "og:locale" }, "en_IN");

    // -- Canonical + og:url + hreflang --------------------------------------
    const path = typeof window !== "undefined" ? (window.location.pathname + window.location.search) : "";
    const canonical = url ? (url.startsWith("http") ? url : `${SITE_ORIGIN}${url}`) : `${SITE_ORIGIN}${path}`;
    setLink("canonical", canonical);
    setLink("alternate", canonical, { hreflang: "en-IN" });
    setLink("alternate", canonical, { hreflang: "x-default" });
    setMeta('meta[property="og:url"]', { property: "og:url" }, canonical);

    // -- Robots — per-route override, else fall back to global default -------
    if (robots) {
      setMeta('meta[name="robots"]', { name: "robots" }, robots);
    }

    // -- Article-specific meta (og:type=article) -----------------------------
    if (article && type === "article") {
      if (article.publishedTime) setMeta('meta[property="article:published_time"]', { property: "article:published_time" }, article.publishedTime);
      if (article.modifiedTime)  setMeta('meta[property="article:modified_time"]',  { property: "article:modified_time"  }, article.modifiedTime);
      if (article.section)       setMeta('meta[property="article:section"]',        { property: "article:section" }, article.section);
      if (Array.isArray(article.tags)) {
        article.tags.forEach((tag, i) => {
          setMeta(`meta[property="article:tag"][data-idx="${i}"]`, { property: "article:tag", "data-idx": String(i) }, tag);
        });
      }
      setMeta('meta[property="article:author"]', { property: "article:author" }, "Dr Tegbir Singh Sidhu");
      setMeta('meta[property="article:publisher"]', { property: "article:publisher" }, "Dr Tegbir Vascular");
    }

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r());
    };
  }, [title, description, image, url, type, robots, JSON.stringify(keywords || []), JSON.stringify(article || null)]);

  return null;
}
